import { Link } from "react-router-dom";
import { docUrl, formatBytes } from "../lib/docUrl";
import {
  attachmentAriaLabel,
  dateParts,
  isExternalHref,
  type Notification,
} from "../lib/notifications";

interface NotificationRowProps {
  item: Notification;
  /** "compact" drops the attachment size and puts date beside category. */
  variant?: "compact" | "full";
}

/**
 * One row of the notice register. Shared by the home block and the
 * /notifications page so the two can never drift apart.
 */
export function NotificationRow({ item, variant = "full" }: NotificationRowProps) {
  const { day, month, year } = dateParts(item.date);
  const compact = variant === "compact";

  const payload = item.attachment ? (
    <a
      className="notif-row-doc"
      href={docUrl(item.attachment.key)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={attachmentAriaLabel(item)}
    >
      <span aria-hidden="true">↓</span> {item.attachment.kind.toUpperCase()}
      {!compact && ` · ${formatBytes(item.attachment.bytes)}`}
    </a>
  ) : item.href && isExternalHref(item.href) ? (
    // Circulars routinely point at an external portal (KEA, COMEDK, VTU).
    // Those must be a plain anchor — <Link> would treat the absolute URL as
    // an in-app path and fall through to the wildcard route.
    <a
      className="notif-row-link"
      href={item.href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open: ${item.title} (external site)`}
    >
      Open <span aria-hidden="true">↗</span>
    </a>
  ) : item.href ? (
    <Link className="notif-row-link" to={item.href} aria-label={`Read: ${item.title}`}>
      Read <span aria-hidden="true">→</span>
    </Link>
  ) : null;

  return (
    <div className={"notif-row" + (compact ? " is-compact" : "")} id={item.id}>
      {compact ? (
        <div className="notif-row-meta">
          {item.category} · {day} {month}
        </div>
      ) : (
        <>
          <div className="notif-row-date">
            {day} {month}
            <span>{year}</span>
          </div>
          <div className="notif-row-cat">{item.category}</div>
        </>
      )}
      <div className="notif-row-title">{item.title}</div>
      <div className="notif-row-payload">
        {item.pinned && <span className="notif-pin">Pinned</span>}
        {payload}
      </div>
    </div>
  );
}
